import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';

import backendAxios from '../../utils/backendAxios';

// La instancia de Stripe se carga una sola vez (a nivel de módulo).
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Estilo del CardElement (su propio formato, no MUI: Stripe lo pinta en su iframe y necesita los valores, no los nombres del tema)
const opcionesDeTarjeta = ({ palette }) => ({
  style: {
    base: {
      fontSize: '16px',
      color: palette.primary.main,
      fontFamily: 'inherit',
      '::placeholder': { color: palette.text.secondary },
    },
    invalid: { color: palette.error.main },
  },
});

// Componente interno: aquí sí están disponibles los hooks de Stripe (dentro de <Elements>).
const CardInner = forwardRef((props, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);
  const theme = useTheme();
  const opciones = useMemo(() => opcionesDeTarjeta(theme), [theme]);

  // BookingContainer llama a confirmar() al apretar "Confirmar reserva".
  // Pide un SetupIntent al backend, guarda/autentica la tarjeta y devuelve el paymentMethodId.
  useImperativeHandle(ref, () => ({
    async confirmar() {
      if (!stripe || !elements) throw new Error('El módulo de pagos aún se está cargando. Espera un momento.');
      const { data } = await backendAxios.post('/api/payments/stripe/setup-intent');
      const resultado = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (resultado.error) {
        throw new Error(resultado.error.message || 'La tarjeta fue rechazada. Verifica los datos.');
      }
      return resultado.setupIntent.payment_method; // pm_...
    },
  }));

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: '10px',
          px: 1.75,
          py: 1.75,
          bgcolor: 'white',
          transition: 'border-color .15s',
          '&:focus-within': { borderColor: 'secondary.main' },
        }}
      >
        <CardElement
          options={opciones}
          onReady={() => setListo(true)}
          onChange={(e) => setError(e.error?.message || '')}
        />
      </Box>
      {error ? (
        <Typography variant="caption" color="error.oscuro" sx={{ display: 'block', mt: 0.75, fontWeight: 600 }}>
          {error}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <LockIcon sx={{ fontSize: 13, color: 'success.oscuro' }} />
          <Typography variant="caption" color="text.secondary">
            {listo ? 'Tu tarjeta se procesa de forma segura con Stripe.' : 'Cargando módulo de pago seguro…'}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

// Envoltura con el provider <Elements>. Reenvía el ref al componente interno.
const StripeCard = forwardRef((props, ref) => (
  <Elements stripe={stripePromise}>
    <CardInner ref={ref} {...props} />
  </Elements>
));

export default StripeCard;
